import React, { useEffect, useState, useCallback } from 'react'
import { useEventCreation, isClient } from '@gopeerproject/chuck'
import {
  useLocalAudioToggle,
  useLocalVideoToggle,
  useMediaContext,
  useRoomState
} from '@gopeerproject/web-kit'
import { createStyleSheet, useStyleSheet, View } from '@gopeerproject/ui-kit'

import { Classroom as ClassroomConstants } from '@constants'
import { Heap, Intercom } from '@helpers'
import { useClassroomEnv } from '@components/ClassroomEnv'

import Intro from './Intro'
import Devices from './Devices'
import {
  useClassroomDispatch,
  useClassroomManager,
  useClassroomQuery
} from '../../hooks'
import { useAuth } from '../../../../hooks'

const STEPS = {
  INTRO: 'INTRO',
  DEVICES: 'DEVICES'
}

const Onboarding = () => {
  const styles = useStyleSheet(getStyles)
  const { env } = useClassroomEnv()
  const { initialize } = useClassroomManager()
  const [step, setStep] = useState(STEPS.INTRO)
  const { connect } = useMediaContext()
  const roomState = useRoomState()
  const classroomDispatch = useClassroomDispatch()
  const [isAudioEnabled] = useLocalAudioToggle()
  const [isVideoEnabled] = useLocalVideoToggle()
  const { profile } = useAuth()
  const { data: classroom } = useClassroomQuery()
  const { mutate } = useEventCreation()

  const {
    data: { twilioToken }
  } = useClassroomQuery()

  const next = useCallback(() => {
    switch (step) {
      case STEPS.INTRO:
        return setStep(STEPS.DEVICES)

      case STEPS.DEVICES:
        initialize()
        Heap.trigger(
          `Classroom - Join - Audio: ${isAudioEnabled} Video: ${isVideoEnabled}`
        )
        Intercom.update({
          'Classroom Audio': isAudioEnabled,
          'Classroom Video': isVideoEnabled
        })
        switch (env) {
          case ClassroomConstants.ENV.PLAYGROUND:
            classroomDispatch({
              type: 'set_step',
              payload: ClassroomConstants.STEPS.READY
            })
            return
          case ClassroomConstants.ENV.CLASSROOM:
            if (classroom.type === 'group')
              mutate({
                location: 'classroom',
                action: getJoinActionName(profile.type),
                externalId: classroom.id
              })
            connect(twilioToken)
            return
          default:
        }
    }
  }, [
    connect,
    classroomDispatch,
    setStep,
    initialize,
    twilioToken,
    env,
    isAudioEnabled,
    isVideoEnabled,
    step,
    mutate,
    classroom.id,
    classroom.type,
    profile.type
  ])

  useEffect(() => {
    if (roomState !== 'disconnected')
      classroomDispatch({
        type: 'set_step',
        payload: ClassroomConstants.STEPS.READY
      })
  }, [roomState, classroomDispatch])

  return (
    <View
      onClick={() => classroomDispatch({ type: 'close_invite_modal' })}
      style={styles.container}
    >
      {step === STEPS.INTRO && <Intro next={next} />}
      {step === STEPS.DEVICES && <Devices next={next} />}
    </View>
  )
}

function getJoinActionName(profileType) {
  switch (true) {
    case isClient(profileType):
      return 'student_join'
    default:
      return `${profileType}_join`
  }
}

const getStyles = createStyleSheet(() => ({
  container: { flexGrow: 1, justifyContent: 'center', alignItems: 'center' }
}))

export default Onboarding
